import React, { ReactElement } from "react";

import { css, cx } from "@emotion/css";
import {
  Link,
  RegisteredRouter,
  ValidateLinkOptions,
  ValidateLinkOptionsArray,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useComponentWidth } from "@/hooks/useComponentSize.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ScreenSizes } from "@/theme/responsive.ts";
import { TooltipMenuAction } from "@/uiKit/atoms/TooltipMenu.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

const MenuItems = [
  {
    id: "patient-sync-index",
    to: "/patient-sync",
    title: "patientSync.indexTitle",
  },
  {
    id: "patient-sync-ongoing-imports",
    to: "/patient-sync/ongoing-imports",
    title: "patientSync.ongoingImportTitle",
  },
] satisfies ValidateLinkOptionsArray;

const routeAlternatives: Record<string, string[]> = {
  "patient-sync-index": ["/patient-sync/libreview-select"],
};

export type PatientSyncMenuProps = {
  className?: string;
};
export const PatientSyncMenu: React.FC<PatientSyncMenuProps> = ({ className }) => {
  const [divRef, width] = useComponentWidth();
  const styles = useStyles(makeStyles);
  const small = width < ScreenSizes.SM;
  const routeMatches = useMatchRoute();
  const selectedItem = MenuItems.find(item => {
    const mainRoute = item.to;
    const alternativeRoutes =
      item.id in routeAlternatives && routeAlternatives[item.id] ? routeAlternatives[item.id] : [];
    return routeMatches({ to: mainRoute }) || alternativeRoutes.some(route => routeMatches({ to: route }));
  });

  return (
    <div
      ref={ref => {
        divRef.current = ref;
      }}
      className={cx(styles.container, className, {
        [styles.menuSmall]: small,
      })}
    >
      {small && <PatientSyncMenuSmall selected={selectedItem} />}
      {!small && <PatientSyncMenuFull selected={selectedItem} />}
    </div>
  );
};

const PatientSyncMenuSmall: React.FC<{ selected: ValidateLinkOptions | undefined }> = ({ selected }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  return (
    <>
      {selected ? (
        <MenuItem id={selected.id ?? ""} link={{ to: selected.to }} selected={true} title={t(selected.title)} />
      ) : null}
      <TooltipMenuAction
        id="patient-sync-menu"
        className={styles.tooltipAction}
        wrapperClassName={styles.tooltipWrapper}
        placement="bottom-start"
        actions={MenuItems.map(item =>
          item.title !== selected?.title ? (
            <SmallMenuItem id={item.id} key={item.title} title={t(item.title)} link={{ to: item.to }} />
          ) : null,
        )}
      >
        <Icons.hamburger className={styles.icon} />
      </TooltipMenuAction>
    </>
  );
};

const PatientSyncMenuFull: React.FC<{ selected: ValidateLinkOptions | undefined }> = ({ selected }) => {
  const { t } = useTranslation();
  return (
    <>
      {MenuItems.map(item => (
        <MenuItem
          id={item.id}
          key={item.title}
          title={t(item.title)}
          selected={selected?.id === item.id}
          link={{ to: item.to }}
        />
      ))}
    </>
  );
};

type MenuItemProps<TOptions> = {
  id: string;
  title: string;
  selected: boolean;
  link: ValidateLinkOptions<RegisteredRouter, TOptions>;
};

const MenuItem = <TOptions,>({ id, title, link, selected }: MenuItemProps<TOptions>): ReactElement => {
  const styles = useStyles(makeMenuItemStyles, selected);
  const navigate = useNavigate();

  return (
    <div id={id} className={styles.menuItem} onClick={() => navigate(link)}>
      <div className={styles.menuItemLink}>
        <Link {...link} />
        <Typography variant="subtitle" className={styles.text}>
          {title}
        </Typography>
      </div>
    </div>
  );
};

type SmallMenuItemProps<TOptions> = {
  id: string;
  title: string;
  link: ValidateLinkOptions<RegisteredRouter, TOptions>;
};

const SmallMenuItem = <TOptions,>({ id, title, link }: SmallMenuItemProps<TOptions>): ReactElement => {
  const styles = useStyles(makeMenuItemStyles, false);

  return (
    <Link id={id} {...link}>
      <Typography variant="body" className={styles.text}>
        {title}
      </Typography>
    </Link>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
  `,
  tooltipAction: css({
    display: "flex",
    alignSelf: "stretch",
    alignItems: "center",
  }),
  tooltipWrapper: css({
    display: "flex",
  }),
  menuSmall: css`
    align-items: center;
  `,
  icon: css`
    margin-left: ${theme.spacing(8)};
    width: ${FontSizes.body};
    height: ${FontSizes.body};
    color: ${theme.palette.text.primary};
  `,
});

const makeMenuItemStyles = (theme: Theme, selected: boolean) => ({
  menuItem: css`
    display: flex;
    flex-direction: column;
    cursor: pointer;
    border-radius: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;

    &:hover {
      background-color: ${theme.palette.background.default};
      a {
        border-bottom: ${!selected ? `${theme.palette.background.paper} ${theme.spacing(1)} solid` : ""};
      }
    }
  `,
  menuItemLink: css`
    flex-grow: 1;
    margin: ${theme.spacing(3)} ${theme.spacing(8)} 0;
    &::after {
      content: "";
      position: relative;
      display: ${selected ? "block" : "none"};
      left: 0;
      right: 0;
      bottom: 0;
      height: ${theme.spacing(1)};
      width: 100%;
      background: ${theme.palette.primary.main};
    }
  `,
  text: css`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: clip;
    font-weight: 600;
    color: ${selected ? theme.palette.primary.main : ""};
  `,
});
